import { useEffect, useState } from 'react';
import * as Styles from '../styles';
import WorkflowCard from '../Cards/WorkflowCard';
import WorkflowContainer from '../WorkflowContainer/WorkflowContainer';
import { WorkflowViewMap, Workflow, Application } from '../../types';
import { RoundedIconButton } from '../Buttons/IconButton';
import { IconExpandLess, IconExpandMore } from 'cdk-radial';
import { useTheme } from '../../contexts/Theme';
import {  useAllApplicationsStore, useUnifyProfileStore, useUserIPRestrictionStore } from '../../Store';
import { useTranslation } from 'react-i18next';
import {IUserIPRestrictionEntity} from '../DealerAdmin/IPRestrictionInterface';
import toTestId from '../../testUtils/toTestId';
import { withStatusIndicator } from '../ApplicationsTab/ApplicationsTab';
import { usePaneManagement } from '../../contexts/PaneManagementContext';

const findIpRestricted = (ipRestriction:IUserIPRestrictionEntity[], workflowId:string,appId:string) =>{

  const app = ipRestriction?.find(
    (item:IUserIPRestrictionEntity) => item.entityType === 'application' 
    && item.entityId === appId 
  );
  if(app?.isRestricted){
    return true;
  }
  if(app?.isIpConfigured){
    return false;
  }
  const data = ipRestriction?.some(
    (item:IUserIPRestrictionEntity) => item.entityType === 'workflow' 
    && item.entityId === workflowId 
    && item.isRestricted
  );
  return data;

}

const getTooltipMsg = (ipRestriction:IUserIPRestrictionEntity[], workflowId:string) =>{
  const data = ipRestriction?.find(
    (item:IUserIPRestrictionEntity) => item.entityType === 'workflow' 
    && item.entityId === workflowId 
    && item.isRestricted
  );
  return data;
}

function DomainGrouping({
    domain,
    isExpanded,
    workflows,
  }: {
    domain?: string;
    isExpanded: boolean;
    workflows: Workflow[];
  }) {
  
  const [newWorkFlows, setNewWorkFlows] = useState<Workflow[]>([]);
  const setDeepLinkProgress = useAllApplicationsStore(state => state.setDeepLinkProgress);
  const { openCtaPane } = usePaneManagement();
  const { unifyProfile } = useUnifyProfileStore();

  const isWorkflowDefault = (workflowId: string): boolean => {
    if (!unifyProfile?.workflows || !Array.isArray(unifyProfile.workflows)) return false;
    return unifyProfile.workflows.some(
      (wf: any) =>
        (wf.workflowId === workflowId ) &&
        (wf.isDefaultWorkflow === true )
    );
  };

  useEffect(() => {
    if (!workflows) return;

    try {
      let timer: any;
      const deepLink = localStorage.getItem('deep-link-url');
      const clickFunction = () => {
        if (deepLink && !deepLink.includes('GS=true')) {
          const deepLinkParams = deepLink.substring(0, deepLink.indexOf('/')).split('_');
          const deepLinkWfId = deepLinkParams[0] === 'wf' ? deepLinkParams[1] : '';
          const deepLinkUrl = deepLink.substring(deepLink.indexOf('/') + 1).replace('?GS=true', '');

          if (deepLinkWfId) {
            const wf = workflows.find((wf) => wf.id === deepLinkWfId);
            const ele = document.getElementById('wf-card-' + toTestId(wf?.name || ''));
            wf && ele?.click();
            setDeepLinkProgress && setDeepLinkProgress(false);
            const linkFunction = () => {
              const frameWindow: any = document.getElementById(deepLinkWfId);
              frameWindow && (frameWindow.src = deepLinkUrl);
              localStorage.removeItem('deep-link-url');
            }
            timer = setTimeout(linkFunction, 2000);
          }
        }
        else {
          const deepLinkUrl = deepLink?.substring(deepLink?.indexOf('/') + 1).replace('?GS=true', '');
          deepLinkUrl && openCtaPane(deepLinkUrl);
          setDeepLinkProgress && setDeepLinkProgress(false);
          const linkFunction = () => {
            const frmWindow: any = document.getElementById("ctapane");
            if (frmWindow) {
              frmWindow.style.visibility = "visible";
              frmWindow.style.setProperty("visibility", "visible", "important");
              frmWindow.classList.remove("hide")
            }
            localStorage.removeItem('deep-link-url');
          }
          timer = setTimeout(linkFunction, 1000);
        }
      }
      const clickTimer = setTimeout(clickFunction, 1400);
      setNewWorkFlows(withStatusIndicator(workflows));
      return () => {
        clearTimeout(clickTimer);
        clearTimeout(timer);
      }
    }
    catch (e) {
      console.log(e);
    }
    // eslint-disable-next-line 
  }, [workflows, setDeepLinkProgress]);
 
  const { t } = useTranslation();
  const ipRestriction = useUserIPRestrictionStore((state: any) => state.ipRestriction);
  const restrictedTooltipMsg = `${t('workflowList.restrictedTooltipMsg')}`; 

  return (
    <div>
      {domain && <Styles.DomainHeader>{domain}</Styles.DomainHeader>}
      <Styles.CardList>
        {newWorkFlows.map((workflow) => (
          <WorkflowCard
            appId={workflow.appId}
            id={workflow.id}
            key={workflow.id}
            name={workflow.name}
            description={workflow.description}
            url={workflow.url}
            isFavorite={workflow.isFavorite}
            isEnabled={workflow.isEnabled ?? true}
            iconColor={workflow.icon || ''}
            iconText={workflow.iconText || ''}
            tabIndex={!isExpanded ? -1 : 0}
            cdkuUrl={workflow.cdkuUrl || ''}
            isRestricted={findIpRestricted(ipRestriction,workflow.id,workflow.appId)}
            restrictedTooltipMsg={getTooltipMsg(ipRestriction,workflow.id)?.message || restrictedTooltipMsg}
            openInNewTab={workflow.openInNewTab}
            showNewIndicator={workflow.showNewIndicator}
            isDefault={isWorkflowDefault(workflow.id)}
            warningMessage={workflow.warningMessage}
            requiredOS={workflow.requiredOS}
          />
        ))}
      </Styles.CardList>
    </div>
  );
}

export default function WorkflowList({
  workflows,
  apps,
}: {
  workflows: WorkflowViewMap;
  apps: Record<string, Application>;
}) {

  const { darkMode } = useTheme();
  const initializeExpandedWorkflows = () => {
    const expandedWorkflows: Record<string, boolean> = {};
    Object.keys(workflows).forEach((applicationTitle) => {
      expandedWorkflows[applicationTitle] = true;
    });
    return expandedWorkflows;
  };

  const [expandedWorkflows, setExpandedWorkflows] = useState<
    Record<string, boolean>
  >(initializeExpandedWorkflows());

  const toggleExpandCollapse = (applicationTitle: string) => {
    setExpandedWorkflows((prevExpandedWorkflows) => ({
      ...prevExpandedWorkflows,
      [applicationTitle]: !prevExpandedWorkflows[applicationTitle],
    }));
  };
  return (
    <div>
      {workflows &&
        Object.entries(workflows).map(([applicationTitle, domain], index) => (
          <WorkflowContainer
            isExpanded={!!expandedWorkflows[applicationTitle]}
            key={index}
            darkMode={darkMode}
          >
            <Styles.AppContainerHeader>
              <Styles.ProductIcon iconColor={apps[applicationTitle]?.icon}>
                {apps[applicationTitle]?.iconText}
              </Styles.ProductIcon>
              {applicationTitle}
              <RoundedIconButton
                icon={
                  !!expandedWorkflows[applicationTitle] ? (
                    <IconExpandLess />
                  ) : (
                    <IconExpandMore />
                  )
                }
                style={{ marginLeft: 'auto' }}
                darkMode={darkMode}
                onClick={() => toggleExpandCollapse(applicationTitle)}
                text=""
              />
            </Styles.AppContainerHeader>
            {Array.isArray(domain) ? (
              <DomainGrouping
                isExpanded={!!expandedWorkflows[applicationTitle]}
                workflows={domain}
              />
            ) : (
              Object.entries(domain).map(([domainTitle, _workflows]) => (
                <DomainGrouping
                  key={domainTitle}
                  domain={domainTitle}
                  // TODO: thought the ternary would narrow this but it doesn't
                  workflows={withStatusIndicator(_workflows) as Workflow[]}
                  isExpanded={!!expandedWorkflows[applicationTitle]}
                />
              ))
            )}
          </WorkflowContainer>
        ))}
    </div>
  );
}
